import { useCallback, useEffect } from 'react';
import { UpgradeMessageEventKeys } from 'ui-addon-upgrades/_core/common/constants/upgradeMessageEventKeys';
import { logRavenException } from '../_core/utils/errors';
import { useUpgradePoint } from './useUpgradePoint';

/**
 * ## Upgrade Management System
 *
 * `useUpgradeModal` takes your upgrade key as an argument and returns a callback
 * to open the Upgrade Page. Upgrade keys can be created and managed in the
 * [Upgrade Management System](<https://tools.hubteamqa.com/ums>) (`go/ums`).
 *
 * @example
 * ```tsx
 * const YourComponent = () => {
 *  const { openUpgradeModal } = useUpgradeModal('your-upgrade-key');
 *
 *  return <Button onClick={openUpgradeModal} />;
 * }
 *
 * @deprecated
 * Replace with useUpgradePoint from ui-addon-upgrades
 * ```
 */
export function useUpgradeModal(upgradeKey, isFeatureLocked = true) {
  const {
    openUpgradePage
  } = useUpgradePoint(upgradeKey, isFeatureLocked);
  return {
    openUpgradeModal: openUpgradePage
  };
}
export function useUpgradeModalInIframe(upgradeKey, isFeatureLocked = true) {
  // Post message to useUpgradeEventListener
  useEffect(() => {
    try {
      window.top.postMessage({
        modalKey: upgradeKey,
        event: UpgradeMessageEventKeys.FETCH_UPGRADE_POINT,
        isFeatureLocked
      }, '*');
    } catch (error) {
      logRavenException({
        customMessage: 'useUpgradeModalInIframe tryCatch error: An error occured with posting the message to the window',
        error,
        uniqueId: upgradeKey
      });
    }
  }, [upgradeKey, isFeatureLocked]);

  // Handle opening the modal
  const openUpgradeModal = useCallback(() => {
    try {
      if (!isFeatureLocked) {
        return;
      }

      // Need to send post message so that the "upgrade point clicked" tracking event can fire
      window.top.postMessage({
        upgradeKey,
        event: UpgradeMessageEventKeys.OPEN_UPGRADE_PAGE,
        isFeatureLocked
      }, '*');
    } catch (error) {
      logRavenException({
        customMessage: 'useUpgradeModalInIframe tryCatch error: An error occured in defining openUpgradeModal',
        error,
        uniqueId: upgradeKey
      });
    }
  }, [isFeatureLocked, upgradeKey]);

  // return the callback for use in app
  return {
    openUpgradeModal
  };
}